import React, {useEffect, useState} from "react";
import {WithOrganization} from "../components/organizations/organization-context";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {addSoldTo, deleteSoldTo, getSoldTos} from "../api/organization-functions";
import {addShipTo, deleteShipTo, getShipTos} from "../api/organization-unit-functions";
import {Button, Grid, TableContainer} from "@material-ui/core";
import RemoveCircle from "@material-ui/icons/RemoveCircle";
import IconButton from "@material-ui/core/IconButton";
import DoneIcon from "@material-ui/icons/Done";
import CloseIcon from "@material-ui/icons/Close";
import FormControl from "@material-ui/core/FormControl";
import * as _ from "lodash";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Table from "@material-ui/core/Table";
import {makeStyles} from "@material-ui/core/styles";
import {TrimmedTextField} from "../components/_shared/TrimmedTextField";
import ReactsCard from "../components/ui/ReactsCard";
import {useApiNotification} from "../components/ui/ApiNotificationProvider";
import {ConfirmationPrompt} from "../components/ui/ConfirmationPrompt";

interface AccountNumber {
    id: number;
    organizationId: number;
    hasError?: boolean;
    errorText?: string;
}

interface SoldTo extends AccountNumber {
    soldToNumber?: number;
}

interface ShipTo extends AccountNumber {
    orgUnitId: number;
    shipToNumber?: number;
}

interface Props<T extends AccountNumber> {
    readonly numbers: Array<T>;
    readonly setNumbers: (accountNumbers: Array<T>) => void;
    readonly setSingleNumber: (accountNumber: T, value?: number) => T;
    readonly displayNumber: (accountNumber: T) => string;
    readonly createNumber: (nbr?: number) => T;
    readonly addNumber: (accountNumber: T) => Promise<T>;
    readonly deleteNumber: (id: number) => Promise<boolean>;
    readonly type: string;
}

function AccountNumbersList<T extends AccountNumber>(props: Props<T>) {
    const {t} = useTranslation();
    const classes = useStyles();
    const {handleError} = useApiNotification();
    const [showDeletePrompt, setShowDeletePrompt] = useState(false);
    const [disableDeletePrompt, setDisableDeletePrompt] = useState(false);
    const [accountNumberToDelete, setAccountNumberToDelete] = useState<T | null>(null);

    const cancelDelete = () => {
        setShowDeletePrompt(false);
        setDisableDeletePrompt(false);
    }

    const confirmDelete = () => {
        setDisableDeletePrompt(true);
        if (accountNumberToDelete) {
            deleteAccountNumber(accountNumberToDelete).then(() => {
                setShowDeletePrompt(false);
                setDisableDeletePrompt(false);
            });
        } else {
            setShowDeletePrompt(false);
            setDisableDeletePrompt(false);
        }
    }

    const triggerDelete = (accountNumber: T) => {
        setAccountNumberToDelete(accountNumber);
        setShowDeletePrompt(true);
        setDisableDeletePrompt(false);
    }

    const deleteAccountNumber = (accountNumber: T) => {
        if (accountNumber.id) {
            return props.deleteNumber(accountNumber.id).then((result) => {
                if (result) {
                    props.setNumbers(props.numbers.filter(x => x.id !== accountNumber.id));
                }
            })
                .catch(handleError);
        } else {
            props.setNumbers(props.numbers.filter(x => x !== accountNumber));
            return Promise.resolve();
        }
    }

    const createAccountNumber = () => {
        const newNumber = props.createNumber();
        props.setNumbers([...props.numbers, newNumber]);
    }

    const addAccountNumber = (accountNumber: T) => {
        if (props.displayNumber(accountNumber) !== "") {
            props.addNumber(accountNumber).then((result: T) => {
                if (result) {
                    props.setNumbers([...props.numbers.filter(x => x.id !== 0), result, ...props.numbers.filter(x => x.id === 0 && x !== accountNumber)]);
                }
            })
                .catch(handleError);
        } else {
            props.setNumbers([...props.numbers.filter(x => x.id !== 0), {
                ...accountNumber,
                hasError: true,
                errorText: t(`_Common.Messages.FieldIsRequired`)
            }, ...props.numbers.filter(x => x.id === 0 && x !== accountNumber)]);
        }
    }

    const setSingleNumber = (accountNumber: T, index: number, value?: string) => {
        const newForm = props.numbers;
        const current = newForm[index];
        const numericValue = parseInt(value ?? "");
        const newItem = props.setSingleNumber(accountNumber, numericValue);

        newItem.hasError = !(/^\s*?[0-9]+\s*?$/.test(value ?? ""));
        if (newItem.hasError) {
            newItem.errorText = value ? t(`_Common.Messages.FieldIsNumeric`) : t(`_Common.Messages.FieldIsRequired`);
        } else {
            newItem.errorText = '';
        }
        const result = [...newForm];
        result[index] = _.merge({}, current, newItem);
        props.setNumbers(result);
    };

    const renderSingleNumber = (prop: { accountNumber: T, index: number, onChange: (accountNumber: T, index: number, value?: string) => void }) => {
        if (prop.accountNumber.id) {
            return <>
                <TableCell
                    className={classes.numberCell}><FormControl>{props.displayNumber(prop.accountNumber)}</FormControl></TableCell>
                <TableCell className={classes.actionCell}>
                    <FormControl>
                        <IconButton aria-label="delete" onClick={() => triggerDelete(prop.accountNumber)}>
                            <RemoveCircle className={classes.deleteActionButton}/>
                        </IconButton>
                    </FormControl>
                </TableCell>
            </>
        } else {
            return <>
                <TableCell className={classes.numberCell}>
                    <FormControl><TrimmedTextField required="required" onFormChange={(name: string, value: string) => {
                        prop.onChange(prop.accountNumber, prop.index, value);
                    }}
                                                   placeholder={t("OrganizationUnits.AccountNumbers.Number")}
                                                   error={prop.accountNumber.hasError}
                                                   helperText={prop.accountNumber.errorText}></TrimmedTextField></FormControl>
                </TableCell>
                <TableCell className={classes.actionCell}>
                    <FormControl>
                        <IconButton aria-label="confirm" onClick={() => addAccountNumber(prop.accountNumber)}
                                    disabled={prop.accountNumber.hasError}>
                            <DoneIcon className={prop.accountNumber.hasError ? '' :  classes.confirmActionButton} aria-disabled={prop.accountNumber.hasError}/>
                        </IconButton>
                    </FormControl>
                    <FormControl>
                        <IconButton aria-label="cancel" onClick={() => deleteAccountNumber(prop.accountNumber)}>
                            <CloseIcon className={classes.deleteActionButton}/>
                        </IconButton>
                    </FormControl>
                </TableCell>
            </>
        }
    }

    return <><TableContainer><Table><TableBody>{props.numbers.map((nb, index) => <TableRow
        key={`${props.type}-${index}`}>
        {renderSingleNumber({accountNumber: nb, index, onChange: setSingleNumber})}
    </TableRow>)}
    </TableBody>
    </Table>
    </TableContainer>
        <Grid item xs={12}><Button variant="contained" disableElevation style={{marginTop: 10}}
                                   disabled={(props.numbers.filter(x => !x.id).length > 0)}
                                   color="primary"
                                   onClick={createAccountNumber}>{t('_Common.Labels.Add')}</Button></Grid>
        <ConfirmationPrompt
            title={t(`OrganizationUnits.AccountNumbers.Remove.Title`)}
            description={t('OrganizationUnits.AccountNumbers.Remove.Description')}
            open={showDeletePrompt}
            disable={disableDeletePrompt}
            onCancel={cancelDelete}
            onConfirm={confirmDelete}
        />
    </>
}

const SoldToList = (props: Props<SoldTo>) => {
    return AccountNumbersList(props);
}

const ShipToList = (props: Props<ShipTo>) => {
    return AccountNumbersList(props);
}

const useStyles = makeStyles(theme => ({
    root: {
        minWidth: 250,
        margin: theme.spacing(4)
    },
    actionCell: {
        textAlign: "right",
        paddingTop: 0,
        paddingBottom: 0
    },
    numberCell: {
        paddingTop: 0,
        paddingBottom: 0
    },
    deleteActionButton: {
        color: theme.palette.error.main
    },
    confirmActionButton: {
        color: theme.palette.success.main,
    },
    addButton: {
        marginTop: 10
    }
}));

export const AccountNumbersView: React.FC<WithOrganization> = ({currentOrganization}) => {
    const {organizationUnitId} = useParams<{ organizationUnitId: string }>();
    const {t} = useTranslation();
    const classes = useStyles();
    const {handleError} = useApiNotification();
    const navigate = useNavigate();
    const [soldTos, setSoldTos] = useState<Array<SoldTo>>([]);
    const [shipTos, setShipTos] = useState<Array<ShipTo>>([]);

    useEffect(() => {
        getSoldTos(currentOrganization.id)
            .then(result => {
                setSoldTos(result);
            })
            .catch(handleError);

        getShipTos(currentOrganization.id, parseInt(organizationUnitId?.toString() ?? "0"))
            .then(result => {
                setShipTos(result);
            })
            .catch(handleError);
    }, []);

    const addSoldToView = (accountNumber: SoldTo) => {
        return addSoldTo(currentOrganization.id, accountNumber.soldToNumber);
    }

    const deleteSoldToView = (id: number) => {
        return deleteSoldTo(currentOrganization.id, id);
    }

    const addShipToView = (accountNumber: ShipTo) => {
        return addShipTo(currentOrganization.id, organizationUnitId && parseInt(organizationUnitId.toString() || "0") || 0, accountNumber.shipToNumber);
    }

    const deleteShipToView = (id: number) => {
        return deleteShipTo(currentOrganization.id, organizationUnitId && parseInt(organizationUnitId.toString() || "0") || 0, id);
    }

    const onClose = () => {
        navigate(`/organization-units/${organizationUnitId}`);
    }

    return <ReactsCard title={t('OrganizationUnits.Menu.AccountNumbers')} className={classes.root}><Grid container
                                                                                                         spacing={3}>
        <Grid item xs={6}>
            <h2>{t("OrganizationUnits.AccountNumbers.SoldTos")}</h2>
            <SoldToList type={"SoldTo"} numbers={soldTos} setNumbers={setSoldTos}
                        displayNumber={(accountNumber) => accountNumber.soldToNumber?.toString() ?? ""}
                        createNumber={(nbr) => {
                            return {id: 0, organizationId: currentOrganization.id, soldToNumber: nbr};
                        }}
                        addNumber={addSoldToView} deleteNumber={deleteSoldToView} setSingleNumber={(nbr, value) => {
                return {...nbr, soldToNumber: value};
            }}></SoldToList>
        </Grid>
        <Grid item xs={6}>
            <h2>{t("OrganizationUnits.AccountNumbers.ShipTos")}</h2>
            <ShipToList type={"ShipTo"} numbers={shipTos} setNumbers={setShipTos}
                        displayNumber={(accountNumber) => accountNumber.shipToNumber?.toString() ?? ""}
                        createNumber={(nbr) => {
                            return {
                                id: 0,
                                organizationId: currentOrganization.id,
                                orgUnitId: organizationUnitId && parseInt(organizationUnitId) || 0,
                                shipToNumber: nbr
                            };
                        }}
                        addNumber={addShipToView} deleteNumber={deleteShipToView} setSingleNumber={(nbr, value) => {
                return {...nbr, shipToNumber: value};
            }}></ShipToList>
        </Grid>
    </Grid>
        <hr/>
        <Grid item xs={12} className={classes.actionCell}><Button variant="outlined" disableElevation
                                                                  style={{marginTop: 10}}
                                                                  color="primary"
                                                                  onClick={onClose}>{t('_Common.Labels.Close')}</Button></Grid>
    </ReactsCard>;
};
